<ui-card [loading]="isLoading">
    <ui-banner
        *ngIf="
            hasTimeClock &&
            timesheetEntry.timeTrackingMethod &&
            timesheetEntry.timeTrackingMethod !== settings.timeTrackingMethod
        "
        class="margin-bottom-3"
        variant="info"
    >
        @if (authService.isAdmin()) {
            <p>
                {{ 'time-tracking.enterHoursCardComponent.timeTrackingMethodWarning' | translate }}
            </p>
        } @else {
            <p>
                {{ 'time-tracking.enterHoursCardComponent.timeTrackingMethodWarningEmployee' | translate }}
            </p>
        }
    </ui-banner>

    <form #form="ngForm">
        <div *ngIf="enteringFor !== 'self'" class="app-grid">
            <mat-form-field class="columns-12">
                <mat-label>{{ 'time-tracking.enterHoursCardComponent.employee' | translate }}</mat-label>
                <input
                    name="employee"
                    type="text"
                    placeholder="{{ 'time-tracking.enterHoursCardComponent.employeePlaceholder' | translate }}"
                    required
                    aria-label="Number"
                    matInput
                    [matAutocomplete]="auto"
                    [formControl]="employeeFormControl"
                    [disabled]="isDisabled()"
                />
                <mat-autocomplete autoActiveFirstOption #auto="matAutocomplete" [displayWith]="displayEmployee">
                    <mat-option *ngFor="let employee of filteredEmployees | async" [value]="employee">
                        {{ employee.fullName }}
                    </mat-option>
                </mat-autocomplete>
            </mat-form-field>
        </div>

        <div class="time-grid">
            <ui-date-picker
                label="{{ 'time-tracking.enterHoursCardComponent.date' | translate }}"
                name="date"
                [ngModel]="timesheetEntry.workedOn"
                (ngModelChange)="onChangeDate($event)"
                placeholder="{{ 'time-tracking.enterHoursCardComponent.datePlaceholder' | translate }}"
                [required]="true"
                #date="ngModel"
                [maxDate]="maxDate"
                [disabled]="isDisabled()"
            ></ui-date-picker>

            <mat-form-field>
                <mat-label appRemoveAriaOwns>{{
                    'time-tracking.enterHoursCardComponent.project' | translate
                }}</mat-label>
                <!-- if project can be changed, show a select -->
                <ng-container *ngIf="projectCanBeChanged">
                    <mat-select
                        name="project"
                        [disabled]="!timesheetEntry.employee || isDisabled()"
                        [(ngModel)]="timesheetEntry.projectId"
                        (ngModelChange)="onChangeProject($event)"
                        #projectSelect="ngModel"
                        placeholder="{{ 'time-tracking.enterHoursCardComponent.projectPlaceholder' | translate }}"
                    >
                        <mat-option *ngIf="allowNoProject" [value]="null">{{
                            'time-tracking.enterHoursCardComponent.none' | translate
                        }}</mat-option>
                        <mat-option
                            [value]="projectAssignment.project.id"
                            *ngFor="
                                let projectAssignment of timesheetEntry.employee
                                    ?.timeTrackingProjectAssignmentsUserCanEnterHoursFor;
                                let i = index
                            "
                        >
                            {{ projectAssignment.project.name | translate }}
                        </mat-option>
                    </mat-select>

                    <mat-error *ngIf="projectCanBeChanged && projectSelect.invalid">{{
                        projectSelect.errors | formErrors
                    }}</mat-error>
                </ng-container>
                <!-- if project cannot be changed, show a disabled input -->
                <input
                    *ngIf="!projectCanBeChanged"
                    type="text"
                    disabled
                    matInput
                    [value]="timesheetEntry.project?.name ?? 'time-tracking.enterHoursCardComponent.none' | translate"
                />
            </mat-form-field>
        </div>

        <div
            class="time-grid"
            [ngClass]="{
                'three-columns': hourTypes.length > 0,
            }"
        >
            <ng-container
                *ngIf="hasTimeClock && timesheetEntry.timeTrackingMethod === 'start_end_time'; else timeInputTemplate"
            >
                <mat-form-field [ngClass]="{ 'start-time': hourTypes.length === 0 }">
                    <mat-label>{{ 'time-tracking.enterHoursCardComponent.startTime' | translate }}</mat-label>
                    <input
                        type="text"
                        matInput
                        placeholder="--:--"
                        name="startTime"
                        [ngModel]="timesheetEntry.startTimeFormatted"
                        #startTime="ngModel"
                        [matAutocomplete]="autoStartTime"
                        (ngModelChange)="filterStartTimes($event); onStartTimeChange($event)"
                        required
                        timeFormatValidator
                        [appOverlapValidator]="overlappingEntries.start"
                        [matAutocompleteComponent]="autoStartTime"
                        [disabled]="isDisabled()"
                        appTimeFormat
                        (valueFormatted)="onSelectStartTime($event)"
                    />
                    <mat-autocomplete #autoStartTime="matAutocomplete">
                        <mat-option *ngFor="let time of filteredStartTimes" [value]="time.label">
                            {{ time.label }}
                        </mat-option>
                    </mat-autocomplete>
                    @if (startTime.invalid) {
                        <mat-error>
                            @if (startTime.errors?.invalidTimeFormat) {
                                {{ 'time-tracking.enterHoursCardComponent.invalidTimeFormat' | translate }}
                            }
                            @if (startTime.errors?.required) {
                                {{ startTime.errors | formErrors }}
                            }
                            @if (startTime.errors?.overlap) {
                                {{ startTime.errors?.overlap | translate }}
                            }
                        </mat-error>
                    }
                </mat-form-field>
                <mat-form-field [ngClass]="{ 'end-time': hourTypes.length === 0 }">
                    <mat-label>{{ 'time-tracking.enterHoursCardComponent.endTime' | translate }}</mat-label>
                    <input
                        type="text"
                        matInput
                        placeholder="--:--"
                        name="endTime"
                        #endTime="ngModel"
                        [ngModel]="timesheetEntry.endTimeFormatted"
                        [matAutocomplete]="autoEndTime"
                        (ngModelChange)="filterEndTimes($event); onEndTimeChange($event)"
                        [appOverlapValidator]="overlappingEntries.end"
                        [matAutocompleteComponent]="autoEndTime"
                        [disabled]="isDisabled()"
                        (valueFormatted)="onSelectEndTime($event)"
                        required
                        timeFormatValidator
                        appTimeFormat
                    />
                    <mat-autocomplete #autoEndTime="matAutocomplete">
                        <mat-option *ngFor="let time of filteredEndTimes" [value]="time.label">
                            {{ time.label }}
                        </mat-option>
                    </mat-autocomplete>
                    @if (endTime.invalid) {
                        <mat-error>
                            @if (endTime.errors?.invalidTimeFormat) {
                                {{ 'time-tracking.enterHoursCardComponent.invalidTimeFormat' | translate }}
                            }
                            @if (endTime.errors?.required) {
                                {{ endTime.errors | formErrors }}
                            }
                            @if (endTime.errors?.overlap) {
                                {{ endTime.errors?.overlap | translate }}
                            }
                            @if (endTime.errors?.endTimeBeforeStart) {
                                {{ 'time-tracking.enterHoursCardComponent.endTimeBeforeStart' | translate }}
                            }
                        </mat-error>
                    }
                </mat-form-field>
            </ng-container>
            <ng-template #timeInputTemplate>
                <mat-form-field [ngClass]="{ 'span-2': hourTypes.length === 0 }">
                    <mat-label>{{ 'time-tracking.enterHoursCardComponent.hoursAndMinutes' | translate }}</mat-label>
                    <input
                        type="text"
                        required
                        matInput
                        #timeInput="ngModel"
                        name="timeInput"
                        placeholder="--:--"
                        [maxlength]="5"
                        [max]="1440"
                        [ngModel]="timeInputDisplayValue()"
                        (ngModelChange)="onTimeInputChange($event)"
                        [ngModelOptions]="{ updateOn: 'blur' }"
                        [disabled]="isDisabled()"
                    />
                    <mat-error *ngIf="timeInput.invalid">{{ timeInput.errors | formErrors }}</mat-error>

                    <!-- stop inputting more than the max number of minutes in a day -->
                    <mat-error *ngIf="timesheetEntry.minutes > 1440">
                        {{ 'time-tracking.enterHoursCardComponent.tooManyMinutes' | translate }}
                    </mat-error>
                </mat-form-field>
            </ng-template>

            <mat-form-field *ngIf="hourTypes.length > 0">
                <mat-label appRemoveAriaOwns>{{
                    'time-tracking.enterHoursCardComponent.hourType' | translate
                }}</mat-label>
                <mat-select
                    name="hourType"
                    [ngModel]="selectedHourType"
                    (ngModelChange)="updateTimesheetBasedOnHourType($event)"
                    [disabled]="isDisabled()"
                    #hourTypeSelect="ngModel"
                    placeholder="{{ 'time-tracking.enterHoursCardComponent.unpaid' | translate }}"
                    [compareWith]="compareHourTypes"
                >
                    <mat-option [value]="null">{{
                        'time-tracking.enterHoursCardComponent.unpaid' | translate
                    }}</mat-option>
                    <mat-option [value]="hourType" *ngFor="let hourType of hourTypes">
                        {{ hourType.displayName | translate }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
        </div>

        <div class="app-grid">
            <mat-form-field class="columns-12">
                <mat-label>{{ 'time-tracking.enterHoursCardComponent.note' | translate }}</mat-label>
                <textarea
                    placeholder="{{ 'time-tracking.enterHoursCardComponent.notePlaceholder' | translate }}"
                    #note="ngModel"
                    name="note"
                    [(ngModel)]="timesheetEntry.note"
                    [disabled]="isDisabled()"
                    matInput
                    [maxlength]="250"
                >
                </textarea>
                <mat-error *ngIf="note.invalid">{{ note.errors | formErrors }}</mat-error>
                <mat-hint>{{ note.control.value?.length ?? 0 }} / 250</mat-hint>
            </mat-form-field>
        </div>
        @if (
            hasTimeClock &&
            settings.timeTrackingMethod === 'start_end_time' &&
            (timesheetEntry.timeTrackingMethod === 'start_end_time' || timesheetEntry.timeTrackingMethod === null)
        ) {
            <div class="formatted-hours margin-top-2">
                <mat-icon class="margin-right-1" svgIcon="timeTrackingClock"></mat-icon>
                <span>{{ formattedHoursAndMinutes() }}</span>
            </div>
        }
    </form>
    <div *ngIf="showRemove" class="flex justify-end">
        <ui-button (click)="removeEntry()" type="danger-ghost">{{ 'remove' | translate }}</ui-button>
    </div>
</ui-card>
